import { createSlice } from '@reduxjs/toolkit';
import { missionActions } from '../mission/missionSlice';
import { replayActions } from '../replay/replaySlice';
import { RootState } from '../../app/store';

export interface CommandObject {
    type: string,
    status: string,
    source: string,
    parameters?: {
        [key: string]: any
    },
    created: string,
    updated: string
};

export interface CommandState {
    commands: {
        [id: string]: CommandObject
    },
    history: any[],
    loading: boolean,
    loaded: boolean,
    error: string | null
};

const initialState: CommandState = {
    commands: {},
    history: [],
    loading: false,
    loaded: false,
    error: null
};

const commandSlice = createSlice({
    name: 'command',
    initialState,
    reducers: {
        send_relay_command: ((state, action) => {}),
        send_buzzer_command: ((state, action) => {}),
        add_or_edit_command_success: ((state, action) => {
            const received = action.payload;
            const current = state.commands[received.id];
            state.commands[received.id] = {
                type: received.type ? received.type : current?.type,
                status: received.status ? received.status : current?.status,
                source: received.source ? received.source : current?.source,
                parameters: received.parameters ? {state: received.parameters.frequency, ...received.parameters} : current?.parameters,
                // ^ buzzer's 'frequency' field acts as their 'state' field
                // if this command does not concern a buzzer, this 'state' value will be overwritten by the one in 'parameters.state'
                created: received.created ? received.created : current?.created,
                updated: received.updated ? received.updated : current?.updated
            };
        }),
        add_or_edit_command_failure: ((state, action) => {
            state.error = `Error ${action.payload.status}: ${action.payload.text}`;
        }),
        get_commands: ((state, action) => {
            state.loading = true;
            state.loaded = false;
            state.error = null;
            state.commands = {};
        }),
        get_commands_success: ((state, action) => {
            state.loading = false;
            state.loaded = true;
            state.error = null;

            const commands: {[id: string]: CommandObject} = {};
            action.payload.commands.forEach((data: any) => {
                commands[data.id] = {
                    type: data.payload.type,
                    status: data.status,
                    source: data.source,
                    parameters: {state: data.payload.frequency, ...data.payload},
                    // ^ buzzer's 'frequency' field acts as their 'state' field
                    // if this command does not concern a buzzer, this 'state' value will be overwritten by the one in 'data.payload.state'
                    created: data.created,
                    updated: data.last_update
                }
            });

            state.commands = commands;
            state.history = action.payload.history ? action.payload.history : state.history;
        }),
        get_commands_failure: ((state, action) => {
            state.loading = false;
            state.loaded = false;
            state.error = `Error ${action.payload.status}: ${action.payload.text}`;
        })
    },
    extraReducers: (builder) => {
        builder.addCase(missionActions.set_active_mission, (state) => {
            Object.assign(state, initialState);
        });
        builder.addCase(replayActions.start, (state) => {
            Object.assign(state, initialState);
        });
        builder.addCase(replayActions.stop, (state) => {
            Object.assign(state, initialState);
        });
    }
});

export const selectCommand = (state: RootState) => state.command;

export const commandActions = commandSlice.actions;

export default commandSlice.reducer;